import type { DatasourceMutationRequest } from "pages/grid/components";

export type EnvLang = { lang: string; envNumeric: number | undefined };

const ADMIN_DECODE_SEGMENTS =
  "heatpumps|accessory|accessories|heatpumpDiscounts|energies|vat|yearloss|projects";
const NON_ADMIN_DECODE_ROOTS = "accessorygroup|accessorymgt|parameters|annex";

export function normalizeExternalUrl(value: unknown): string | null {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return null;
  if (/^[a-z][a-z\d+.-]*:\/\//i.test(trimmed)) return trimmed;
  if (/^\/\//.test(trimmed)) return `https:${trimmed}`;
  if (/^[^\s/]+\.[^\s]+$/i.test(trimmed) || /^[^\s/]+\.[^\s]+\//i.test(trimmed)) {
    return `https://${trimmed}`;
  }
  return null;
}

export const replaceParams = (template: string, params: Record<string, string | number>) => {
  let result = template || "";
  Object.keys(params).forEach((key) => {
    result = result.replace(new RegExp(`##${key}##`, "g"), String(params[key]));
    result = result.replace(new RegExp(`\\{${key}\\}`, "g"), String(params[key]));
    result = result.replace(new RegExp(`:${key}\\b`, "g"), String(params[key]));
  });
  return result;
};

export function resolveEnvNumericId(
  environnements: any[],
  storedEnvId: string | null,
  storedEnvCode: string
): number | undefined {
  if (storedEnvId && storedEnvId !== "undefined" && storedEnvId !== "null" && storedEnvId.trim() !== "") {
    const n = Number(storedEnvId);
    if (!Number.isNaN(n)) return n;
  }
  if (/^\d+$/.test(storedEnvCode)) {
    const n = Number(storedEnvCode);
    if (!Number.isNaN(n)) return n;
  }
  const found = environnements.find(
    (e: any) => e.code === storedEnvCode || String(e.id) === storedEnvCode
  );
  if (found?.id != null) return Number(found.id);
  if (environnements[0]?.id != null) return Number(environnements[0].id);
  return undefined;
}

export function joinBaseUrlAndPath(baseUrl: string, path: string): string {
  const b = baseUrl.replace(/\/+$/, "");
  const p = path.replace(/^\/+/, "");
  if (!b) return p;
  if (!p) return b;
  return `${b}/${p}`;
}

export function usesDecodeParamsEnvSegment(rawEndpoint: string): boolean {
  return (
    new RegExp(`/admin/(?:${ADMIN_DECODE_SEGMENTS})\\b`, "i").test(rawEndpoint) ||
    /(^|\/)accessory\/?$/i.test(rawEndpoint) ||
    /(^|\/)accessory\/##/i.test(rawEndpoint) ||
    /\/(accessorygroup|accessorymgt)\b/i.test(rawEndpoint) ||
    /\/parameters\b/i.test(rawEndpoint) ||
    /(^|\/)annex\//i.test(rawEndpoint) ||
    /(^|\/)annex$/i.test(rawEndpoint)
  );
}

export function normalizeBareNumericEnvPath(
  endpoint: string,
  envNumeric: number,
  lang: string
): string {
  const composite = `env=${envNumeric}&lang=${lang}`;
  let out = endpoint.replace(
    new RegExp(`(^|\\/)admin\\/(${ADMIN_DECODE_SEGMENTS})\\/(\\d+)(?=\\/|$|\\?)`, "gi"),
    (_m, lead, route) => {
      const r = /^accessories$/i.test(route) ? "accessory" : route;
      return `${lead}admin/${r}/${composite}`;
    }
  );
  out = out.replace(
    new RegExp(`(^|\\/)(${NON_ADMIN_DECODE_ROOTS})\\/(\\d+)(?=\\/|$|\\?)`, "gi"),
    (_m, lead, seg) => `${lead}${seg}/${composite}`
  );
  out = out.replace(
    new RegExp(`(^|\\/)(${ADMIN_DECODE_SEGMENTS})\\/(\\d+)(?=\\/|$|\\?)`, "gi"),
    (_m, lead, route) => {
      const r = /^accessories$/i.test(route) ? "accessory" : route;
      return `${lead}${r}/${composite}`;
    }
  );
  return out;
}

export function replaceTrailingNumericWithEnvLang(
  baseUrl: string,
  endpoint: string,
  envNumeric: number,
  lang: string
): string {
  const composite = `env=${envNumeric}&lang=${lang}`;
  const ep = endpoint.replace(/^\/+|\/+$/g, "");
  if (!/^\d+$/.test(ep)) return endpoint;
  const baseNoTrailing = baseUrl.replace(/\/+$/, "");
  if (
    new RegExp(`/admin/(?:${ADMIN_DECODE_SEGMENTS})$`, "i").test(baseNoTrailing) ||
    new RegExp(`/(?:${NON_ADMIN_DECODE_ROOTS})$`, "i").test(baseNoTrailing)
  ) {
    return composite;
  }
  return endpoint;
}

export function buildMutationRequest(
  action: { active?: boolean; endpoint?: string; method?: string } | undefined,
  fallbackMethod: string,
  baseUrl: string,
  envLang: EnvLang
): DatasourceMutationRequest | null {
  if (!action?.active) return null;
  if (!action.endpoint) return null;

  const { lang, envNumeric } = envLang;
  const rawEndpoint = action.endpoint;
  const useComposite =
    usesDecodeParamsEnvSegment(rawEndpoint) ||
    usesDecodeParamsEnvSegment(baseUrl) ||
    /##env##/.test(rawEndpoint);

  const params: Record<string, string | number> = { lang };
  if (envNumeric != null && !Number.isNaN(envNumeric)) {
    const envComposite = `env=${envNumeric}&lang=${lang}`;
    params.env = useComposite ? envComposite : String(envNumeric);
  }

  let endpoint = replaceParams(rawEndpoint, params);
  const hasEnvPlaceholder = /\{env\}|:env\b|##env##/.test(rawEndpoint);
  if (!hasEnvPlaceholder && endpoint.endsWith("/") && envNumeric != null && !Number.isNaN(envNumeric)) {
    const envComposite = `env=${envNumeric}&lang=${lang}`;
    endpoint = `${endpoint}${useComposite ? envComposite : String(envNumeric)}`;
  }

  if (envNumeric != null && !Number.isNaN(envNumeric)) {
    endpoint = replaceTrailingNumericWithEnvLang(baseUrl, endpoint, envNumeric, lang);
    endpoint = normalizeBareNumericEnvPath(endpoint, envNumeric, lang);
  }

  endpoint = endpoint.replace(/\/+$/, "");
  return {
    url: joinBaseUrlAndPath(baseUrl, endpoint),
    method: action.method || fallbackMethod,
  };
}

export function buildTableDetectionSource(
  tableTitle: string,
  tableWidgetName: unknown,
  selectedDataSource: any
): string {
  return [
    tableTitle,
    tableWidgetName,
    selectedDataSource?.name,
    selectedDataSource?.baseUrl,
    selectedDataSource?.read?.endpoint,
    selectedDataSource?.create?.endpoint,
    selectedDataSource?.update?.endpoint,
    selectedDataSource?.trash?.endpoint,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}
